import { Alert, Button, Stack } from "@mui/material";
import React, { createContext, useContext, useEffect } from "react";
import { RecoilState, useRecoilState } from "recoil";
import { IdName, MasterAgentSearchTag, PropallyAgentV1Api } from "../api/generated/src";
import { IdNameMaster, MasterAgentSearchTagsState, MasterInterestedPropertyTypesState } from "../view_models/atoms";
import { MasterDataKey, masterDataStatusState } from "../view_models/masterData";

const fetchInterestedPropertyTypes = (): Promise<IdName[]> =>
  new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1MasterDataInterestedPropertyTypesGet(
      (error: object, data: IdName[], response: object) => (error ? reject(response ?? error) : resolve(data || []))
    );
  });

const fetchAgentSearchTags = (): Promise<MasterAgentSearchTag[]> =>
  new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1MasterDataAgentSearchTagsGet(
      (error: object, data: MasterAgentSearchTag[], response: object) =>
        error ? reject(response ?? error) : resolve(data || [])
    );
  });

function useMasterDataResource<T>(
  key: MasterDataKey,
  dataState: RecoilState<T[]>,
  fetchData: () => Promise<T[]>,
  onAuthenticationError: (error: unknown) => boolean
) {
  const [data, setData] = useRecoilState(dataState);
  const [status, setStatus] = useRecoilState(masterDataStatusState(key));
  // status を effect の依存にすると loading への更新で自分の通信をキャンセルするため、
  // 明示した試行番号を起点にする。成功/失敗は画面遷移をまたいでキャッシュする。
  const [attempt, setAttempt] = React.useState(0);
  const statusRef = React.useRef(status);
  statusRef.current = status;
  // 画面遷移で認証エラーハンドラーが変わっても、進行中の共有取得は継続する。
  const authenticationErrorHandler = React.useRef(onAuthenticationError);
  useEffect(() => {
    authenticationErrorHandler.current = onAuthenticationError;
  }, [onAuthenticationError]);

  useEffect(() => {
    if (statusRef.current === "ready" || statusRef.current === "error") return;
    let active = true;
    setStatus("loading");
    fetchData()
      .then((result) => {
        if (!active) return;
        setData(result);
        setStatus("ready");
      })
      .catch((error) => {
        if (!active || authenticationErrorHandler.current(error)) return;
        // API の障害通知は interceptor が担当する。console 経由では重複送信しない。
        setStatus("error");
      });
    return () => {
      active = false;
      setStatus((current) => (current === "loading" ? "idle" : current));
    };
  }, [attempt, fetchData, setData, setStatus]);

  const retry = () => {
    if (statusRef.current === "loading") return;
    statusRef.current = "idle";
    setStatus("idle");
    setAttempt((value) => value + 1);
  };
  return { data, isLoading: status === "loading" || status === "idle", error: status === "error", retry };
}

type Resource<T> = { data: T[]; isLoading: boolean; error: boolean; retry: () => void };
const emptyResource = { data: [], isLoading: false, error: false, retry: () => undefined };
const MasterDataContext = createContext<{
  interestedPropertyTypes: Resource<IdNameMaster>;
  agentSearchTags: Resource<MasterAgentSearchTag>;
}>({ interestedPropertyTypes: emptyResource, agentSearchTags: emptyResource });

export const useMasterData = () => useContext(MasterDataContext);

// 認証が確認された保護画面だけでマウントする。多数の一覧行・検索欄から同じマスタを
// 参照しても通信とエラー案内は各マスタにつき1つにする。
export const MasterDataProvider: React.FC<
  React.PropsWithChildren<{ onAuthenticationError: (error: unknown) => boolean }>
> = ({ children, onAuthenticationError }) => {
  const interestedPropertyTypes = useMasterDataResource(
    "interestedPropertyTypes",
    MasterInterestedPropertyTypesState,
    fetchInterestedPropertyTypes,
    onAuthenticationError
  );
  const agentSearchTags = useMasterDataResource(
    "agentSearchTags",
    MasterAgentSearchTagsState,
    fetchAgentSearchTags,
    onAuthenticationError
  );
  const failures = [
    { label: "物件種別", resource: interestedPropertyTypes },
    { label: "検索タグ", resource: agentSearchTags },
  ].filter(({ resource }) => resource.error);

  return (
    <MasterDataContext.Provider value={{ interestedPropertyTypes, agentSearchTags }}>
      {failures.length > 0 && (
        <Stack spacing={1} sx={{ position: "fixed", top: 16, right: 16, maxWidth: "calc(100% - 32px)", zIndex: 1500 }}>
          {failures.map(({ label, resource }) => (
            <Alert
              key={label}
              severity="error"
              action={
                <Button color="inherit" onClick={resource.retry}>
                  再試行
                </Button>
              }
            >
              {label}を取得できませんでした。通信状況を確認し、再試行してください。
            </Alert>
          ))}
        </Stack>
      )}
      {children}
    </MasterDataContext.Provider>
  );
};
